import {
  ITrackingData,
  ITrackingObject,
  ODL2ITrackingMandatoryData,
  ODL2ITrackingObject,
} from "../../types/globals";
import { sanitizeSensitiveData } from "./tracking-utils";

export class AdobeAnalyticsTrackingService {
  private static instance: AdobeAnalyticsTrackingService;

  static getInstance(): AdobeAnalyticsTrackingService {
    if (!AdobeAnalyticsTrackingService.instance) {
      AdobeAnalyticsTrackingService.instance =
        new AdobeAnalyticsTrackingService();
    }
    return AdobeAnalyticsTrackingService.instance;
  }

  private constructor() {
    // Private constructor -> singleton
  }

  track(
    trackingData: ITrackingData,
    trackingApps: Record<string, any> = {},
  ): void {
    const SCS = window.SCS;

    if (!SCS?.datalayer?.track || !SCS.datalayer.cloneMandatoryData) {
      return;
    }

    try {
      const mandatoryData = SCS.datalayer.cloneMandatoryData("2.0.0");

      SCS.datalayer.track(
        this.prepareODL2TrackingData(mandatoryData, trackingData, trackingApps),
      );
    } catch (e) {
      console.error("Could not track object.", e);
    }
  }

  prepareTrackingData(
    trackingData: ITrackingData,
    trackingApps: Record<string, any> = {},
    mandatoryDataODL2: ODL2ITrackingMandatoryData,
  ): ITrackingObject {
    return {
      ...mandatoryDataODL2.otherVersions["version 1.0.0"],
      event: {
        ...trackingData,
        label: sanitizeSensitiveData(trackingData.label),
      },
      apps: trackingApps,
    };
  }

  prepareODL2TrackingData(
    mandatoryData: ODL2ITrackingMandatoryData,
    trackingData: ITrackingData,
    trackingApps: Record<string, any> = {},
  ): ODL2ITrackingObject {
    const odl1DataObject = this.prepareTrackingData(
      trackingData,
      trackingApps,
      mandatoryData,
    );

    return {
      ...mandatoryData,
      eventDetails: {
        type: trackingData.action,
        location: trackingData.location,
        category: trackingData.category,
        detail: sanitizeSensitiveData(trackingData.label),
      },
      search: trackingApps.search || {},
      otherVersions: {
        "version 1.0.0": odl1DataObject,
      },
    };
  }

  trackByDataset(dataset?: DOMStringMap): void {
    if (!dataset) return;

    const trackingData: ITrackingData = {
      location: "header",
      action: dataset.trackAction || dataset.trackingAction || "link",
      category: dataset.trackCategory || dataset.trackingCategory || "",
      label: dataset.trackLabel || dataset.trackingLabel || "",
    };

    if (trackingData.category && trackingData.label) {
      this.track(trackingData);
    }
  }

  trackByEvent(event: Event): void {
    const target = event && (event.currentTarget as HTMLElement);
    const dataset = target && target.dataset;
    this.trackByDataset(dataset);
  }
}
